import { zodResolver } from "@hookform/resolvers/zod";
import { resetPassword } from "@stryker/core/api/accounts";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import ArrowIcon from "@/assets/icons/arrow.svg?react";
import KeyIcon from "@/assets/icons/key.svg?react";
import MailIcon from "@/assets/icons/mail.svg?react";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { useToast } from "@/components/ui/use-toast";
import useLocationQuery from "@/hooks/useLocationQuery";

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Must be a valid email" }),
});

type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>;
export default function ForgotPassword() {
  const navigate = useNavigate();
  const query = useLocationQuery();
  const email = query.get("email");
  const { mutate, isPending } = useMutation({
    mutationFn: resetPassword,
    onSuccess: (response, data) => {
      navigate(`/forgot-password?email=${data.email}`, { replace: true });
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  if (email) {
    return <CheckYourEmail email={email} />;
  }

  const onReset = (model: { email: string }) => {
    mutate(model);
    navigate(`/forgot-password?email=${model.email}`, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="flex w-[360px] flex-col">
        <div className="mb-5 self-center rounded-lg border border-solid border-gray-200 p-3">
          <KeyIcon />
        </div>
        <Typography variant="display-sm" weight="semibold" className="text-center">
          Forgot password?
        </Typography>
        <Typography variant="text-md" weight="regular" className="mb-5 text-center">
          No worries, we’ll send you reset instructions.
        </Typography>
        <form onSubmit={handleSubmit(onReset)}>
          <TextInput
            label="Email"
            placeholder="Enter your email"
            type="email"
            error={errors.email?.message}
            {...register("email")}
          />
          <div className="mb-8 mt-6 flex flex-col gap-2">
            <Button disabled={isPending} type="submit">
              Reset password
            </Button>
            <Button
              onClick={() => {
                navigate("/login");
              }}
              type="button"
              variant="ghost"
              className="flex items-center justify-center gap-2"
            >
              <ArrowIcon />
              Back to Login
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CheckYourEmail({ email }: { email: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: resetPassword,
    onError: (error) => {
      toast({ variant: "destructive", title: "Error", description: error?.toString() });
    },
  });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="flex w-[360px] flex-col">
        <div className="mb-5 self-center rounded-lg border border-solid border-gray-200 p-3">
          <MailIcon />
        </div>
        <Typography variant="display-sm" weight="semibold" className="text-center">
          Check your email
        </Typography>
        <Typography variant="text-md" weight="regular" className="text-center">
          We sent a password reset link to
        </Typography>
        <Typography weight="bold" component="span" className="mb-5 text-center">
          {email}
        </Typography>
        <div className="mb-8 flex flex-col items-center gap-2">
          <div className="flex items-center">
            <Typography variant="text-small" weight="regular" className="text-gray-600">
              Didn’t receive the email?
            </Typography>
            <Button
              variant="ghost"
              size="default"
              disabled={isPending}
              onClick={() => {
                mutate({ email });
              }}
            >
              <Typography className="text-primary-700" weight="semibold" variant="text-small">
                Click to resend
              </Typography>
            </Button>
          </div>
          <Button
            onClick={() => {
              navigate("/login");
            }}
            type="button"
            variant="ghost"
            className="flex items-center justify-center gap-2"
          >
            <ArrowIcon />
            Back to Login
          </Button>
        </div>
      </div>
    </div>
  );
}
